import { useEffect, useRef } from 'react';

export default function PhilosophyChapter01() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up, .fade-up-item').forEach((el) => el.classList.add('visible'));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const positions = [
    { icon: 'ri-scales-3-line', text: '治療を「売る」より、「判断軸」を示す' },
    { icon: 'ri-microscope-line', text: '治療より先に、診断を優先する' },
    { icon: 'ri-forbid-line', text: 'できないことを曖昧にしない' },
    { icon: 'ri-shield-check-line', text: '「ワイヤー矯正」が最適な場合、正直に伝える' },
  ];

  return (
    <section ref={sectionRef} id="chapter1" className="py-20 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12 fade-up">
          <p className="text-lg font-bold mb-4" style={{ color: '#3E5F8F' }}>01</p>
          <h2 className="text-3xl md:text-4xl font-normal text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>
            私たちは、治療を「売る」ために診断していません。
          </h2>
          <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
            {`当院は、
マウスピース矯正を
「今すぐ決断させる商品」として扱っていません。

なぜなら、歯並びを整えることは、
一生の健康を左右する重大な医療行為だからです。
私たちの診断において最も優先されるのは、
医学的な根拠に基づく「判断軸」を示すことにあります。`}
          </p>
        </div>

        <div className="mb-12 bg-gray-50 rounded-lg p-8 fade-up">
          <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>私たちの立ち位置</h3>
          <p className="text-base text-gray-700 mb-8">
            さいわいデンタルクリニックは、<br />次のように考えています。
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {positions.map((pos, i) => (
              <div key={i} className="flex items-start gap-4 bg-white p-6 rounded-lg transition-all duration-500 hover:-translate-y-1 fade-up-item">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                  <i className={`${pos.icon} text-2xl`} style={{ color: '#3E5F8F' }} />
                </div>
                <p className="text-sm text-gray-800 font-medium">{pos.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12 fade-up">
          <p className="text-base text-gray-700 leading-relaxed whitespace-pre-line">
            {`マウスピース矯正には物理的な限界があります。
もしあなたの骨格や歯根の状態から、
ワイヤー矯正の方が確実に良い結果を得られると
判断した場合は、
正直にその事実をお伝えします。

できないことを曖昧にせず、
10年後のあなたにとって最も無理のない選択肢を示すこと。
それが歯科医師としての責任だと
私たちは考えています。

「安易に始めず、しっかり比較検討した上で
納得して選択してほしい」

これは単なる願いではなく、
生涯の健康を預かる歯科医師としての決意です。
プロの視点ですべてを伝えた結果、
もし「今はやらない」という判断になったなら、
それもまた一つの正しい医療の在り方だと考えています。`}
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-8 fade-up" style={{ backgroundColor: '#EEF3F9' }}>
          <div className="flex items-start gap-6">
            <img
              alt="理事長"
              className="w-24 h-24 rounded-full object-cover flex-shrink-0"
              src="https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/e76709526e5a74de30833ffcc41ae720.jpeg"
            />
            <div>
              <p className="text-sm text-gray-700 leading-relaxed">
                治療を急がせないことも、大切な医療の一部だと考えています。<br />
                ブランド名や費用の安さに惑わされず、一生の健康を守るための<br />
                「判断軸」を提供したい。<br />
                それが私たちさいわいデンタルクリニックのねがいです。<br /><br />
                医療法人mirai 理事長 谷口正昭
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}